import * as React from 'react';
import { ActionButton } from 'office-ui-fabric-react';
import { IPredecessorInfo, ITask, ITaskAttrs, isExternalPredecessor } from '../../../entities/Subentities';

import TaskPredecessorEditBox from './TaskPredecessorEditBox';
import TaskPredecessor from './TaskPredecessor';
import { Dictionary } from '../../../entities/common';
import { nameof } from '../../../store/services/metadataService';
import { ProjectInfo } from '../../../store/ProjectsListStore';

import { buildPredecessorSnapshot } from '../../utils/duration';
import { toDictionaryById } from '../../utils/common';
import { fetchExternalPredecessorTasks } from '../taskFetchers';
import { Resource } from '../../../store/ResourcesListStore';
import { CalendarDataSet } from '../../../store/calendar/common';

interface Props {
    readonly: boolean;
    task: ITask;
    tasks: ITask[];
    project?: ProjectInfo;
    calendar: CalendarDataSet;
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay?: boolean;
    resources: Dictionary<Resource>;
    calendarsById: Dictionary<CalendarDataSet>;
    useResourceCalendar: boolean;
    onChange: (fieldName?: string, fieldValue?: unknown, extra?: Dictionary<unknown>) => void;
}

const TaskPredecessors = (props: Props) => {
    // calendar / resources / useResourceCalendar / shiftZeroDayMilestone… are consumed by the edit box via {...props}:
    // this component no longer derives dates itself (see applyPredecessorChange).
    const { readonly, tasks, task, onChange, project } = props;
    const allowExternalPredecessors = !project?.isPrivate;

    const predecessors = React.useMemo(() => task.attributes.Predecessor ?? [], [task.attributes.Predecessor]);
    const tasksMap = React.useMemo(() => toDictionaryById(tasks ?? []), [tasks]);
    const [externalTasksMap, setExternalTasksMap] = React.useState<Dictionary<ITask>>({});

    React.useEffect(() => {
        if (!project?.id || project.isArchived || !predecessors.length) {
            setExternalTasksMap({});
            return;
        }

        const hasExternalPredecessors = predecessors.some(isExternalPredecessor);

        if (!hasExternalPredecessors) {
            setExternalTasksMap({});
            return;
        }

        void fetchExternalPredecessorTasks(project.id, task.id)
            .then(loadedTasks => {
                setExternalTasksMap(toDictionaryById(loadedTasks));
            })
            .catch(() => {
                setExternalTasksMap({});
            });
    }, [predecessors, project?.id, project?.isArchived, task.id]);

    const predecessorTasksMap = React.useMemo(() => ({ ...externalTasksMap, ...tasksMap }), [externalTasksMap, tasksMap]);

    const [createPanelIsShown, setCreatePanelVisible] = React.useState<boolean>(false);
    const [createExternalPanelIsShown, setCreateExternalPanelVisible] = React.useState<boolean>(false);
    const showCreatePanel = React.useCallback(() => setCreatePanelVisible(true), []);
    const showCreateExternalPanel = React.useCallback(() => {
        if (!allowExternalPredecessors) {
            return;
        }

        setCreateExternalPanelVisible(true);
    }, [allowExternalPredecessors]);

    const hideCreatePanel = React.useCallback(() => setCreatePanelVisible(false), []);
    const hideCreateExternalPanel = React.useCallback(() => setCreateExternalPanelVisible(false), []);

    const [predecessorToEdit, setPredecessorToEdit] = React.useState<IPredecessorInfo>();
    const cancelPredecessorEdit = React.useCallback(() => setPredecessorToEdit(undefined), []);

    // Send only the Predecessor list — the same minimal payload every other edit-panel field sends (Start, Duration,
    // Effort, the Assignments tab). The server derives Start/Due/Duration from the dependency itself, and the panel's
    // central derivation previews them (Fields.buildDeriveDisplayAttributes applies applyPredecessors, and treats
    // Predecessor as a Fixed Units recalc trigger, over `changes`). Bundling the client-derived triple made a
    // predecessor edit read as an explicit SPAN edit — on Fixed Units that recomputes Effort instead of preserving it
    // (Bug 17036), and a requested span suppresses the span contraction — and that payload is what answered with a
    // 500 for an assignee whose work week is disjoint from the project's (Bug 17135).
    const applyPredecessorChange = React.useCallback((updatedPredecessors: IPredecessorInfo[] | undefined) => {
        onChange(nameof<ITaskAttrs>("Predecessor"), updatedPredecessors);
    }, [onChange]);

    const onRemove = React.useCallback((predecessor: IPredecessorInfo) => {
        applyPredecessorChange(task.attributes.Predecessor?.filter(_ => predecessor !== _));
    }, [task, applyPredecessorChange]);

    const onCreate = (predecessor: IPredecessorInfo) => {
        applyPredecessorChange([...(task.attributes.Predecessor?.map(_ => ({ ..._ })) || []), predecessor]);
        hideCreatePanel();
        hideCreateExternalPanel();
    };

    const onUpdate = (predecessorId: string, predecessor: IPredecessorInfo) => {
        applyPredecessorChange(task.attributes.Predecessor?.map(_ => ({ ..._.id === predecessorId ? predecessor : _ })));
        cancelPredecessorEdit();
    };

    const onAccept = React.useCallback((predecessorId: string) => {
        const predecessorTask = predecessorTasksMap[predecessorId];
        if (!predecessorTask) {
            return;
        }

        const updatedPredecessors = task.attributes.Predecessor?.map(current => {
            if (current.id !== predecessorId) {
                return current;
            }
            return { ...current, ...buildPredecessorSnapshot(current.type, predecessorTask.attributes) };
        });

        applyPredecessorChange(updatedPredecessors);
    }, [predecessorTasksMap, task.attributes, applyPredecessorChange]);

    return <div className="task-predecessors-tab">
        {predecessors.map(_ => predecessorToEdit === _
            ? <TaskPredecessorEditBox
                key={_.id}
                {...props}
                predecessor={_}
                isExternal={isExternalPredecessor(_)}
                externalTask={predecessorTasksMap[_.id]}
                onApply={(updated) => onUpdate(_.id, updated)}
                onCancel={cancelPredecessorEdit} />
            : <TaskPredecessor
                key={_.id}
                predecessor={_}
                task={predecessorTasksMap[_.id]}
                isExternal={isExternalPredecessor(_)}
                readonly={readonly}
                allowEdit={!predecessorToEdit && !createPanelIsShown && !createExternalPanelIsShown}
                onAccept={onAccept}
                onEdit={setPredecessorToEdit}
                onRemove={onRemove}
            />
        )}

        {!readonly && !createPanelIsShown && !createExternalPanelIsShown &&
            <div className="task-predecessors-actions">
                <ActionButton disabled={!!predecessorToEdit} iconProps={{ iconName: "Add" }} onClick={showCreatePanel}>Add Predecessor</ActionButton>
                {allowExternalPredecessors &&
                    <ActionButton disabled={!!predecessorToEdit} iconProps={{ iconName: "Add" }} onClick={showCreateExternalPanel}>Add External Predecessor</ActionButton>
                }
            </div>
        }

        {!readonly && !predecessorToEdit && createPanelIsShown
            && <TaskPredecessorEditBox {...props} onApply={onCreate} onCancel={hideCreatePanel} />}

        {!readonly && allowExternalPredecessors && !predecessorToEdit && createExternalPanelIsShown
            && <TaskPredecessorEditBox {...props} isExternal onApply={onCreate} onCancel={hideCreateExternalPanel} />}
    </div>;
}
export default TaskPredecessors;